import { useGLTF } from "@react-three/drei";
import { useSafeGLTF } from "../../../utils/safeGLTFLoader";
import { GroupProps, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { PI } from "../../../constants";
import { DimensionSectionHorizontal } from "../DimensionSectionHorizontal";
import React, { FunctionComponent, memo, useEffect, useMemo, useRef } from "react";
import { DoubleSide } from "three";
import { Plane } from "three/src/math/Plane";
import { useSizes } from "../../../hooks/useSizes";
import { SectionWidth } from "../../../shared";
import { useColors } from "../../../hooks/useColors";
import { useStoreSystem } from "../../../store";
import shallow from "zustand/shallow";
import { useShelvingStore } from "../../../store/useShelvingStore";
import { Utils } from "../../../utils/utils";

interface PropTypes extends GroupProps {
    isIndent: boolean;
    width: SectionWidth;
    height: number;
    offset: number;
    sectionId: string;
    isFirstRack?: boolean;
}

type HelpersProps = {
    localPlane: Plane;
};

//Хелпер для обрезки стоек
const Helpers: FunctionComponent<HelpersProps> = ({ localPlane }) => {
    const { scene } = useThree();
    const helpers = new THREE.Group();
    helpers.add(new THREE.PlaneHelper(localPlane, 2, 0xff0000));
    helpers.visible = true;
    scene.add(helpers);
    return null;
};

export const Rack = memo(({ position, sectionId, isIndent = false, isFirstRack = false, ...props }: PropTypes) => {
    const parts = useStoreSystem((state) => state.parts, shallow);
    const { getCurrentShelving, updateConnectorYPositions } = useShelvingStore(
        (state) => ({
            getCurrentShelving: state.getCurrentShelving,
            updateConnectorYPositions: state.updateConnectorYPositions,
        }),
        shallow
    );

    const currentShelving = getCurrentShelving();
    const rackConnectorPositionsRef = useRef<number[] | null>(null);

    const glb600 = useSafeGLTF(
        parts?.find((part) => part.NAME.toLowerCase().includes("вертикальная направляющая 600"))?.PROPERTIES.FILE_3D ||
            ""
    );
    const glb800 = useSafeGLTF(
        parts?.find((part) => part.NAME.toLowerCase().includes("вертикальная направляющая 800"))?.PROPERTIES.FILE_3D ||
            ""
    );
    const glb1200 = useSafeGLTF(
        parts?.find((part) => part.NAME.toLowerCase().includes("вертикальная направляющая 1200"))?.PROPERTIES.FILE_3D ||
            ""
    );

    const glbConnector = useSafeGLTF(
        parts?.find((part) => part.NAME.toLowerCase().includes("соединитель"))?.PROPERTIES.FILE_3D || ""
    );

    const { colorsItems, colorsShade } = useColors();
    const { roomHeight, roomDepth, roomLength } = useSizes();

    const totalHeight = currentShelving.height;
    const offsetCeiling = currentShelving.offsetCeiling;

    const material600 = glb600.materials["material_main"].clone();
    const material800 = glb800.materials["material_main"].clone();
    const material1200 = glb1200.materials["material_main"].clone();

    const materialConnector = glbConnector.materials["main_material"].clone();

    material600.side = DoubleSide;
    material800.side = DoubleSide;
    material1200.side = DoubleSide;
    materialConnector.side = DoubleSide;

    material600.color = colorsShade[colorsItems.guides];
    material800.color = colorsShade[colorsItems.guides];
    material1200.color = colorsShade[colorsItems.guides];
    materialConnector.color = colorsShade[colorsItems.brackets];

    material600.metalness = 0.5;
    material800.metalness = 0.5;
    material1200.metalness = 0.5;
    materialConnector.metalness = 0.5;

    material600.roughness = 0.5;
    material800.roughness = 0.5;
    material1200.roughness = 0.5;
    materialConnector.roughness = 0.5;

    let minRemainder: any;

    const racks = useMemo(() => {
        const height = props.height;

        let count600H = Math.ceil(height / 600);
        let count800H = Math.ceil(height / 800);
        let count1200H = Math.ceil(height / 1200);

        minRemainder = { 600: 0, 800: 0, 1200: 0, value: Infinity };

        for (let i = 0; i <= count600H; i++) {
            for (let j = 0; j <= count800H; j++) {
                for (let k = 0; k <= count1200H; k++) {
                    const totalLengthRack = i * 600 + j * 800 + k * 1200;
                    if (totalLengthRack < height) {
                        continue;
                    }
                    if (totalLengthRack / height > 2) {
                        continue;
                    }
                    const tempMinRemainder = totalLengthRack - height;
                    if (minRemainder.value > tempMinRemainder) {
                        minRemainder = { 600: i, 800: j, 1200: k, value: tempMinRemainder };
                    }
                }
            }
        }
        const resultRacks: any[] = [];
        // @ts-ignore
        const [x, y, z] = position;
        let yPos = y;

        const rackConnectorPositions: number[] = [];

        const addRackConnectorPositions = (rackConnectorPositions: number[], yPos: number) => {
            if (yPos >= Utils.myRound((roomHeight - offsetCeiling) / 1000)) {
                return;
            }
            rackConnectorPositions.push(Utils.myRound(yPos), Utils.myRound(yPos + 0.05));
        };

        for (let i = 0; i < minRemainder["600"]; i++) {
            const rack = (
                <group position={[x, yPos, z]} key={`${i}${props.offset}600`}>
                    <mesh
                        rotation={[0, 0, PI]}
                        castShadow
                        receiveShadow
                        geometry={glb600.nodes["600"].geometry}
                        material={material600}
                    />
                    {i !== 0 && (
                        <mesh
                            geometry={glbConnector.nodes.connector.geometry}
                            material={materialConnector}
                            position={[-0.002, 0, 0.02]}
                            castShadow
                            receiveShadow
                            scale={[0.01, 0.01, 0.01]}
                        />
                    )}
                </group>
            );
            addRackConnectorPositions(rackConnectorPositions, yPos);
            yPos -= 0.6;
            resultRacks.push(rack);
        }
        for (let i = 0; i < minRemainder["800"]; i++) {
            const rack = (
                <group position={[x, yPos, z]} key={`${i}${props.offset}800`}>
                    <mesh
                        rotation={[0, 0, PI]}
                        castShadow
                        receiveShadow
                        geometry={glb800.nodes["800"].geometry}
                        material={material800}
                    />
                    {(i !== 0 || (minRemainder["600"] !== 0 && i === 0)) && (
                        <mesh
                            geometry={glbConnector.nodes.connector.geometry}
                            material={materialConnector}
                            position={[-0.002, 0, 0.02]}
                            castShadow
                            receiveShadow
                            scale={[0.01, 0.01, 0.01]}
                        />
                    )}
                </group>
            );
            addRackConnectorPositions(rackConnectorPositions, yPos);
            yPos -= 0.8;
            resultRacks.push(rack);
        }
        for (let i = 0; i < minRemainder["1200"]; i++) {
            const rack = (
                <group position={[x, yPos, z]} key={`${i}${props.offset}1200`}>
                    <mesh
                        rotation={[0, 0, PI]}
                        castShadow
                        receiveShadow
                        geometry={glb1200.nodes["1200"].geometry}
                        material={material1200}
                    />
                    {(i !== 0 || ((minRemainder["800"] !== 0 || minRemainder["600"] !== 0) && i === 0)) && (
                        <mesh
                            geometry={glbConnector.nodes.connector.geometry}
                            material={materialConnector}
                            position={[-0.002, 0, 0.018]}
                            castShadow
                            receiveShadow
                            scale={[0.01, 0.01, 0.01]}
                        />
                    )}
                </group>
            );
            addRackConnectorPositions(rackConnectorPositions, yPos);
            yPos -= 1.2;
            resultRacks.push(rack);
        }
        rackConnectorPositionsRef.current = rackConnectorPositions;
        // rackConnectorPositions.length > 0 && updateConnectorYPositions(rackConnectorPositions, sectionId, isFirstRack); //TODO подумать может куда перенести это
        return resultRacks;
    }, [
        props.height,
        props.width,
        totalHeight,
        roomHeight,
        offsetCeiling,
        roomLength,
        roomDepth,
        colorsItems,
        position,
    ]); //add position

    useEffect(() => {
       rackConnectorPositionsRef.current &&
            updateConnectorYPositions(rackConnectorPositionsRef.current, sectionId, isFirstRack);
    }, [racks]);

    if (!position) {
        return null;
    }

    if (isIndent && props.width) {
        return <DimensionSectionHorizontal isIndent={isIndent} width={props.width} position={position} />;
    }

    let localPlane;

    if (minRemainder && minRemainder.value) {
        localPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -(roomHeight - props.height - offsetCeiling) / 1000);
        material600.clippingPlanes = [localPlane];
        material600.clipShadows = true;
        material600.clipIntersection = true;
        material800.clippingPlanes = [localPlane];
        material800.clipShadows = true;
        material800.clipIntersection = true;
        material1200.clippingPlanes = [localPlane];
        material1200.clipShadows = true;
        material1200.clipIntersection = true;
    }
    return (
        <>
            <group>{racks}</group>
        </>
    );
});

// для подготовки модели:
// уменьшить модель в 100 раз.
// apply scale, rotation, location
// установить направление в минус Y.
// стойка - центр внизу
// полки выравнивать по верхней поверхности и задней грани (геометрию в режиме редактирования Modelling)
// имя стойки - rack, имя материала material_main (у всех).
// если есть еще какой-то элемент, например кронштейн - называть у всех одинаково, как и материал к нему. (support, material_support)
// имя полки - shelf, материала material_main
// всем установить origin в 0
